import { Loader2, MessageSquare } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { createComment, getComments } from '@/services/reviewService';

import type { Comment } from '@/types/comment.types';

interface NotesTabProps {
  applicationId: string;
}

export function NotesTab({ applicationId }: NotesTabProps) {
  const { t } = useTranslation();

  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState<'internal' | 'ops'>('internal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    setIsLoading(true);
    setHasLoadError(false);

    getComments(applicationId)
      .then((data) => {
        if (!isCancelled) {
          setComments(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setHasLoadError(true);
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [applicationId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!text.trim()) return;

      setIsSubmitting(true);
      setHasSubmitError(false);

      try {
        const newComment = await createComment({
          applicationId,
          text: text.trim(),
          visibility,
        });
        setComments((prev) => [newComment, ...prev]);
        setText('');
      } catch {
        setHasSubmitError(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [applicationId, text, visibility],
  );

  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[200px]'>
        <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
      </div>
    );
  }

  if (hasLoadError) {
    return (
      <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>
        {t('submissionDetail.notes.errorLoading')}
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Comments list */}
      {sortedComments.length === 0 ? (
        <div className='bg-white shadow rounded-lg p-12 flex flex-col items-center justify-center text-center'>
          <MessageSquare className='h-12 w-12 text-gray-300 mb-4' />
          <p className='text-sm text-gray-500'>{t('submissionDetail.notes.noComments')}</p>
        </div>
      ) : (
        <div className='space-y-4'>
          {sortedComments.map((comment) => (
            <div key={comment.commentId} className='bg-white shadow rounded-lg p-4'>
              <div className='flex items-center justify-between mb-2'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium text-gray-900'>
                    {comment.author.firstName} {comment.author.lastName}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                      comment.visibility === 'internal' ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-700',
                    )}>
                    {comment.visibility === 'internal'
                      ? t('submissionDetail.notes.internal')
                      : t('submissionDetail.notes.ops')}
                  </span>
                </div>
                <span className='text-xs text-gray-500'>{new Date(comment.createdAt).toLocaleString()}</span>
              </div>
              <p className='text-sm text-gray-700 whitespace-pre-wrap'>{comment.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add comment form */}
      <form onSubmit={(e) => void handleSubmit(e)} className='bg-white shadow rounded-lg p-4 space-y-4'>
        {hasSubmitError ? (
          <div className='bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700'>
            {t('submissionDetail.notes.errorAdding')}
          </div>
        ) : null}

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('submissionDetail.notes.addPlaceholder')}
          disabled={isSubmitting}
          rows={3}
          className='w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
        />

        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <span className='text-xs text-gray-500'>{t('submissionDetail.notes.visibilityLabel')}:</span>
            <button
              type='button'
              disabled={isSubmitting}
              onClick={() => setVisibility('internal')}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded transition-colors',
                visibility === 'internal'
                  ? 'bg-gray-200 text-gray-800'
                  : 'bg-white text-gray-500 border border-gray-300 hover:bg-gray-50',
              )}>
              {t('submissionDetail.notes.internal')}
            </button>
            <button
              type='button'
              disabled={isSubmitting}
              onClick={() => setVisibility('ops')}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded transition-colors',
                visibility === 'ops'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-white text-gray-500 border border-gray-300 hover:bg-gray-50',
              )}>
              {t('submissionDetail.notes.ops')}
            </button>
          </div>

          <button
            type='submit'
            disabled={isSubmitting || !text.trim()}
            className='px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'>
            {isSubmitting ? t('submissionDetail.notes.submitting') : t('submissionDetail.notes.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
